import { useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trackEvent } from '@/segment/segment';
import { Campaign } from '@/shared/types/campaigns';
import { EventName } from '@/shared/types/segment';
import { isKeyEnter } from '@/util/util';

import { useBulkDeleteCampaigns } from '../../hooks/useCampaigns';

interface BulkDeleteCampaignsModalProps {
  /** The selected campaigns; `null`/empty keeps the dialog closed. */
  campaigns: Campaign[] | null;
  onClose: () => void;
  /** Fires only when every DELETE succeeded — a partial failure is not a success signal. */
  onDeleted?: () => void;
  /**
   * Radix restores focus to the opener on close, but the selection-bar trash button unmounts
   * with the selection — pass a handler to send focus somewhere that still exists.
   */
  onCloseAutoFocus?: (event: Event) => void;
}

export const BulkDeleteCampaignsModal = ({
  campaigns,
  onClose,
  onDeleted,
  onCloseAutoFocus,
}: BulkDeleteCampaignsModalProps) => {
  const { t } = useTranslation();
  const { bulkDeleteCampaignsAsync, bulkDeleteStatus } = useBulkDeleteCampaigns();

  const [confirmText, setConfirmText] = useState('');

  const confirmWord = 'Delete';
  const isConfirmed = confirmText.toLowerCase() === confirmWord.toLowerCase();
  const isPending = bulkDeleteStatus === 'pending';
  const count = campaigns?.length ?? 0;

  // Radix keeps the content mounted through the exit animation; remember the last real count so
  // the subtitle doesn't flash "0" while the dialog animates out.
  const [lastCount, setLastCount] = useState(count);
  if (count > 0 && count !== lastCount) {
    setLastCount(count);
  }
  const displayCount = count > 0 ? count : lastCount;

  // `isPending` comes from the last committed render — a held Enter or a double click can
  // re-enter handleDelete before React flushes the pending state. The ref closes that window.
  const inFlightRef = useRef(false);

  const closeModal = () => {
    setConfirmText('');
    onClose();
  };

  const handleDelete = async () => {
    if (!campaigns?.length || inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;

    const campaignIds = campaigns.map((campaign) => campaign.campaign_id);
    trackEvent(EventName.ConfirmBulkDeleteCampaigns, {
      campaign_ids: campaignIds,
      selected_count: campaignIds.length,
    });

    try {
      await bulkDeleteCampaignsAsync(campaignIds);
      onDeleted?.();
    } catch {
      // A partial failure rejects here; the mutation's onError already surfaced the toast.
      // The catch is required — without it the rejection escapes as unhandled.
    } finally {
      inFlightRef.current = false;
      closeModal();
    }
  };

  return (
    // While the fan-out is in flight the dialog must not be dismissable (ESC/overlay): the
    // deletes would keep running headless and the late onDeleted could move the page under
    // a user who has navigated on.
    <Dialog
      open={count > 0}
      onOpenChange={() => {
        if (!isPending) {
          closeModal();
        }
      }}
    >
      <DialogContent
        className="gap-6 sm:max-w-md"
        onCloseAutoFocus={onCloseAutoFocus}
        // Hide rather than leave an X that looks live but is swallowed by the guard above.
        showCloseButton={!isPending}
      >
        <DialogHeader>
          <DialogTitle>{t('campaigns.bulk_delete_title', { count: displayCount })}</DialogTitle>
          <DialogDescription>
            {t('campaigns.bulk_delete_subtitle', { count: displayCount })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Label htmlFor="confirm-bulk-delete">
            {t('campaigns.type_delete_to_confirm', { 0: confirmWord })}
          </Label>
          <Input
            id="confirm-bulk-delete"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (isKeyEnter(e) && isConfirmed && !isPending) {
                e.preventDefault();
                handleDelete();
              }
            }}
            autoComplete="off"
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline-neutral" onClick={closeModal} disabled={isPending}>
            {t('common.back_to_safety')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!isConfirmed}
            isLoading={isPending}
          >
            {t('common.delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
